import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface OnboardingProfile {
  onboarding_completed: boolean;
  onboarding_step: number;
  hints_dismissed: boolean;
}

export function useOnboarding(userId: string | null) {
  const [profile, setProfile] = useState<OnboardingProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!userId) { setLoading(false); return; }
    const { data } = await supabase
      .from("user_profiles")
      .select("onboarding_completed, onboarding_step, hints_dismissed")
      .eq("user_id", userId)
      .maybeSingle();
    // null → no DB row → user predates onboarding; don't force modal on them
    setProfile(data);
    setLoading(false);
  }, [userId]);

  useEffect(() => { fetch(); }, [fetch]);

  const complete = useCallback(async () => {
    if (!userId) return;
    await supabase.from("user_profiles").upsert({
      user_id: userId,
      onboarding_completed: true,
      onboarding_completed_at: new Date().toISOString(),
    }, { onConflict: "user_id" });
    setProfile((p) => p ? { ...p, onboarding_completed: true } : null);
  }, [userId]);

  const dismissHints = useCallback(async () => {
    if (!userId) return;
    await supabase.from("user_profiles").upsert({
      user_id: userId,
      hints_dismissed: true,
    }, { onConflict: "user_id" });
    setProfile((p) => p ? { ...p, hints_dismissed: true } : null);
  }, [userId]);

  return {
    onboardingNeeded: !loading && profile !== null && !profile.onboarding_completed,
    hintsNeeded: !loading && profile !== null && profile.onboarding_completed && !profile.hints_dismissed,
    loading,
    complete,
    dismissHints,
    refetch: fetch,
  };
}
